import { Card, CardContent } from "@/components/ui/card";
import { useMoneyFormat } from "@/hooks/use-money-format";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RecordContextProvider,
  useGetList,
  useGetOne,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { useMemo, useState } from "react";
import { Briefcase } from "lucide-react";
import { Link, useParams } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";

import { findDealLabel } from "../deals/dealUtils";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import "@/styles/mobile-glass.css";
import { plainText, stageProgress, toLocalDate } from "./mobileModel";

/** Report amounts in whole units ("12.300 €"), in the app locale. */
const useWholeMoney = () => {
  const { formatMoney } = useMoneyFormat();
  return { format: (value: number) => formatMoney(Math.round(value)) };
};

export const MobileReports = () => {
  const translate = useTranslate();
  const { dealStages } = useConfigurationContext();
  const [stage, setStage] = useState("");
  const { data: deals, isPending } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "updated_at", order: "DESC" },
    filter: { "archived_at@is": null },
  });
  const { data: delivery } = useGetList<{ id: Deal["id"]; progress: number }>(
    "deals_delivery",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const open = useMemo(
    () => (deals ?? []).filter((deal) => !deal.archived_at),
    [deals],
  );
  const visible = useMemo(
    () => open.filter((deal) => stage === "" || deal.stage === stage),
    [open, stage],
  );
  const stageCounts = useMemo(() => countByStage(open), [open]);
  const total = visible.reduce((sum, deal) => sum + (deal.amount ?? 0), 0);
  const money = useWholeMoney();
  const stageOptions = [
    {
      value: "",
      label: translate("crm.mobile.all_stages"),
      count: open.length,
    },
    ...dealStages
      .filter((item) => stageCounts.has(item.value) || item.value === stage)
      .map((item) => ({
        value: item.value,
        label: item.label,
        count: stageCounts.get(item.value) ?? 0,
      })),
  ];

  return (
    <>
      <MobileHeader>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {translate("crm.mobile.reports_title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {translate("crm.mobile.reports_subtitle")}
          </p>
        </div>
      </MobileHeader>
      <MobileContent>
        <div className="home-m pb-2">
          <div
            role="radiogroup"
            aria-label={translate("crm.mobile.reports_stage_filter")}
            className="home-chips py-1"
          >
            {stageOptions.map((option) => {
              const active = option.value === stage;
              return (
                <button
                  key={option.value || "all"}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  data-active={active ? "true" : undefined}
                  onClick={() => setStage(option.value)}
                  className="glass-chip"
                >
                  {option.label}
                  <span className="glass-chip__count">{option.count}</span>
                </button>
              );
            })}
          </div>

          {isPending ? (
            <Skeleton className="h-28 w-full rounded-[26px]" />
          ) : (
            <div className="home-next" aria-live="polite">
              <span className="home-next__meta">
                <Briefcase aria-hidden className="size-4" strokeWidth={1.75} />
                {stage === ""
                  ? translate("crm.mobile.reports_pipeline")
                  : findDealLabel(dealStages, stage)}
              </span>
              <span className="text-3xl font-semibold tracking-tight tabular-nums">
                {money.format(total)}
              </span>
              <span className="home-next__kind">
                {translate("crm.mobile.reports_count", {
                  smart_count: visible.length,
                })}
              </span>
            </div>
          )}

          <section className="home-m__section" aria-labelledby="reports-list">
            <div className="home-m__head">
              <h2 id="reports-list">
                {translate("crm.mobile.reports_list_title")}
              </h2>
            </div>
            {isPending ? (
              <Skeleton className="h-48 w-full rounded-[26px]" />
            ) : visible.length === 0 ? (
              <div className="home-m__card">
                <p className="home-empty">{translate("crm.mobile.no_deals")}</p>
              </div>
            ) : (
              <div className="home-m__card">
                {visible.map((deal) => (
                  <ReportRow
                    key={deal.id}
                    deal={deal}
                    amount={money.format(deal.amount ?? 0)}
                    progress={dealProgress(deal, delivery, dealStages)}
                    progressLabel={translate("crm.mobile.progress")}
                    stageLabel={
                      findDealLabel(dealStages, deal.stage) ?? deal.stage
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </MobileContent>
    </>
  );
};

const countByStage = (deals: readonly Deal[]) => {
  const counts = new Map<string, number>();
  for (const deal of deals)
    counts.set(deal.stage, (counts.get(deal.stage) ?? 0) + 1);
  return counts;
};

const dealProgress = (
  deal: Deal,
  delivery: readonly { id: Deal["id"]; progress: number }[] | undefined,
  dealStages: Parameters<typeof stageProgress>[1],
) => {
  const delivered = delivery?.find(
    (row) => String(row.id) === String(deal.id),
  )?.progress;
  return typeof delivered === "number"
    ? Math.max(0, Math.min(100, Math.round(delivered)))
    : stageProgress(deal.stage, dealStages);
};

const ReportRow = ({
  deal,
  amount,
  progress,
  progressLabel,
  stageLabel,
}: {
  deal: Deal;
  amount: string;
  progress: number;
  progressLabel: string;
  stageLabel: string;
}) => (
  <Link to={`/informes/${deal.id}`} className="report-row">
    <div className="report-row__top">
      <div className="min-w-0">
        <p className="report-row__title">{deal.name}</p>
        <p className="report-row__subtitle">
          <RecordContextProvider value={deal}>
            <ReferenceField
              source="company_id"
              reference="companies"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </RecordContextProvider>
          {" · "}
          {stageLabel}
        </p>
      </div>
      <p className="report-row__amount">{amount}</p>
    </div>
    <div className="report-progress">
      <div
        className="report-progress__track"
        role="progressbar"
        aria-label={progressLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <div
          className="report-progress__bar"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="report-progress__value">{progress}%</span>
    </div>
  </Link>
);

export const MobileDealShow = () => {
  const translate = useTranslate();
  const { id } = useParams();
  const { dealStages, dealCategories } = useConfigurationContext();
  const [locale = "es"] = useLocaleState();
  const {
    data: deal,
    isPending,
    error,
  } = useGetOne<Deal>("deals", { id: id ?? "" }, { enabled: id != null });
  const { data: deliveryRows } = useGetList<{
    id: Deal["id"];
    progress: number;
  }>("deals_delivery", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "id", order: "ASC" },
  });
  const delivered = deliveryRows?.find(
    (row) => String(row.id) === String(id),
  )?.progress;

  const progress =
    typeof delivered === "number"
      ? Math.max(0, Math.min(100, Math.round(delivered)))
      : deal
        ? stageProgress(deal.stage, dealStages)
        : 0;
  const money = useWholeMoney();
  // Delivered milestones when the deal has them, else its pipeline stage.
  const progressLabel = translate(
    typeof delivered === "number"
      ? "crm.mobile.delivery_progress"
      : "crm.mobile.stage_progress",
  );

  return (
    <>
      <MobileHeader>
        <MobileBackButton to="/informes" />
        <h1 className="flex-1 truncate text-xl font-semibold tracking-tight">
          {deal?.name ?? translate("crm.mobile.reports_title")}
        </h1>
      </MobileHeader>
      <MobileContent>
        {isPending ? (
          <Skeleton className="h-48 w-full rounded-4xl" />
        ) : error || !deal ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.mobile.deal_missing")}
          </p>
        ) : (
          <Card className="rounded-4xl">
            <CardContent className="gap-4">
              <RecordContextProvider value={deal}>
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link="show"
                >
                  <span className="text-sm font-medium text-primary">
                    <TextField source="name" />
                  </span>
                </ReferenceField>
              </RecordContextProvider>
              <p className="text-3xl font-semibold tracking-tight tabular-nums">
                {money.format(deal.amount ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">
                {findDealLabel(dealStages, deal.stage)}
                {deal.categories?.length
                  ? ` · ${deal.categories
                      .map(
                        (value) =>
                          dealCategories.find((item) => item.value === value)
                            ?.label ?? value,
                      )
                      .join(", ")}`
                  : ""}
              </p>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                  <span id="report-progress-label">{progressLabel}</span>
                  <span className="font-medium tabular-nums text-foreground">
                    {progress}%
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-labelledby="report-progress-label"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                  className="h-1.5 overflow-hidden rounded-full bg-muted"
                >
                  <div
                    className="h-full rounded-full bg-brand"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              {deal.description ? (
                <p className="text-sm leading-relaxed">
                  {plainText(deal.description, 600)}
                </p>
              ) : null}
              {deal.expected_closing_date ? (
                <p className="text-sm text-muted-foreground">
                  {new Intl.DateTimeFormat(locale, {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  }).format(toLocalDate(deal.expected_closing_date))}
                </p>
              ) : null}
            </CardContent>
          </Card>
        )}
      </MobileContent>
    </>
  );
};
